import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import Container from '../../layout/Container';
import SectionHeading from '../../common/SectionHeading';
import { hpLogo, dellLogo, lenovoLogo, ciscoLogo, appleLogo, samsungLogo } from '../../../utils/ImageImports';

const hardwareBrands = [
  { name: 'HP', logo: hpLogo },
  { name: 'Dell', logo: dellLogo },
  { name: 'Lenovo', logo: lenovoLogo },
  { name: 'Cisco', logo: ciscoLogo },
  { name: 'Apple', logo: appleLogo },
  { name: 'Samsung', logo: samsungLogo },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const MultiSupport = () => {
  return (
    <section className="py-20 bg-white">
      <Container>
        <SectionHeading
          title="Multi-Brand Hardware Support"
          subtitle="We provide comprehensive support for a wide range of hardware brands to ensure your technology needs are met."
          center
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Custom-made Care Package</h3>
            <p className="text-gray-600 mb-6">
              At Bytematic, we understand that every business has unique IT requirements. That's why we offer custom-made care packages tailored to your specific needs and budget.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="mt-1 mr-3 text-green-500">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Preventive Maintenance</h4>
                  <p className="text-gray-600 text-sm">
                    Regular system checks and updates to prevent potential issues before they occur.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="mt-1 mr-3 text-green-500">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Emergency Response</h4>
                  <p className="text-gray-600 text-sm">
                    Rapid response team available to address critical hardware failures and system outages.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="mt-1 mr-3 text-green-500">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Hardware Replacement</h4>
                  <p className="text-gray-600 text-sm">
                    Quick replacement of faulty hardware to minimize downtime and maintain productivity.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="mt-1 mr-3 text-green-500">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Scalable Solutions</h4>
                  <p className="text-gray-600 text-sm">
                    Flexible support options that can grow and adapt as your business expands.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
          
          <div>
            <p className="text-gray-700 mb-8">
              We've partnered with leading hardware manufacturers to provide you with the best technology solutions available in the market.
            </p>
            
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-3 gap-6"
            >
              {hardwareBrands.map((brand, index) => (
                <motion.div 
                  key={index} 
                  variants={item}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center h-32"
                >
                  <img 
                    src={brand.logo} 
                    alt={`${brand.name} logo`}
                    className="h-12 w-auto mb-3 object-contain"
                  />
                  <p className="font-semibold">{brand.name}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default MultiSupport;